import { Funnel } from "@phosphor-icons/react";
import clsx from "clsx";
import { SortOrder } from "./../api/common/models";
import { Location } from "./../api/models/Location";
import { useLocations } from "./../api/location";
import useCollapsibleContent from "./../hooks/useCollapsibleContent";
import Button from "./Button";
import FilterChip from "./FilterChip";
import MultiSelectDropdown, {
    MultiSelectDropdownItemOption,
} from "./MultiSelectFilterToolbarDropdown";
import ResponsiveGroupedItemsCarousel from "./ResponsiveGroupedItemsCarousel";
import SearchBar from "./SearchBar";
import Separator from "./Separator";
import FilterChipGroup from "./FilterChipGroup";
import { toggleItemInArray } from "./../utils/toggleItemInArray";
import { dataStatusOptions, scopeOptions } from "../constants";
import { useEmissionsSourceItems } from "../api/emissions-sources";
import {
    EmissionsSourceCategory,
    EmissionsSourceItem,
    EmissionsSourceOrderBy,
    EmissionsSourceType,
} from "../api/models/EmissionsSource";

type EmissionsSourcesFilterProps = {
    locationIds: number[];
    scopes: number[];
    item: number[];
    type: number[];
    category: number[];
    dataStatus: number[];
    search: string;
    orderBy: EmissionsSourceOrderBy;
    sortOrder: SortOrder;
    additionalActions?: React.ReactNode;
    hideDataStatusFilter?: boolean;
    setLocationIds: (value: number[]) => void;
    setScopes: (value: number[]) => void;
    setItem: (value: number[]) => void;
    setType: (value: number[]) => void;
    setCategory: (value: number[]) => void;
    setDataStatus: (value: number[]) => void;
    setSearch: (value: string) => void;
};

const convertLocationsToMultiSelectDropdownOptions = (
    locations: Location[]
): MultiSelectDropdownItemOption<number>[] =>
    locations.map((location) => ({
        label: location.name,
        value: location.id,
    }));

const convertEmissionsSourceItemsToMultiSelectDropdownOptions = (
    emissionsSourceItems: EmissionsSourceItem[],
    emissionsSourceTypes: EmissionsSourceType[]
): MultiSelectDropdownItemOption<number>[] => {
    const multiItemTypes: EmissionsSourceItem[] = [];

    emissionsSourceTypes.forEach((type) => {
        const itemsInType = emissionsSourceItems.filter(
            (item) => item.typeId === type.id
        );

        if (itemsInType.length > 1) multiItemTypes.push(...itemsInType);
    });

    return multiItemTypes.map((item) => ({
        label: item.name,
        value: item.id,
    }));
};

const convertEmissionsSourceTypesToMultiSelectDropdownOptions = (
    emissionsSourceItems: EmissionsSourceItem[],
    emissionsSourceTypes: EmissionsSourceType[],
    emissionsSourceCategories: EmissionsSourceCategory[]
): MultiSelectDropdownItemOption<number>[] => {
    const selectedTypes: EmissionsSourceType[] = [];
    const remainingTypes: EmissionsSourceType[] = [];

    emissionsSourceCategories.forEach((category) => {
        const typesInCategory = emissionsSourceTypes.filter(
            (type) => type.categoryId === category.id
        );

        if (typesInCategory.length > 1) selectedTypes.push(...typesInCategory);
        else remainingTypes.push(...typesInCategory);
    });

    remainingTypes.forEach((type) => {
        const itemsInType = emissionsSourceItems.filter(
            (item) => item.typeId === type.id
        );

        if (itemsInType.length > 1) selectedTypes.push(type);
    });

    selectedTypes.sort((type1, type2) => type1.name.localeCompare(type2.name));

    return selectedTypes.map((type) => ({
        label: type.name,
        value: type.id,
    }));
};

const convertEmissionsSourceCategoriesToMultiSelectDropdownOptions = (
    emissionsSourceCategories: EmissionsSourceCategory[]
): MultiSelectDropdownItemOption<number>[] =>
    emissionsSourceCategories.map((emissionsSourceCategory) => ({
        label: emissionsSourceCategory.name,
        value: emissionsSourceCategory.id,
    }));

const EmissionsSourcesFilter = ({
    search,
    setSearch,
    locationIds,
    setLocationIds,
    scopes,
    setScopes,
    item,
    setItem,
    type,
    setType,
    category,
    setCategory,
    dataStatus,
    setDataStatus,
    additionalActions,
    hideDataStatusFilter,
}: EmissionsSourcesFilterProps) => {
    const { locations } = useLocations();
    const { emissionsSourceItems } = useEmissionsSourceItems();

    const { expanded, toggle, triggerProps, expandableContentProps } =
        useCollapsibleContent();

    const locationOptions =
        convertLocationsToMultiSelectDropdownOptions(locations);

    const emissionsSourceItemOptions =
        convertEmissionsSourceItemsToMultiSelectDropdownOptions(
            emissionsSourceItems?.emissionsSourceItems ?? [],
            emissionsSourceItems?.emissionsSourceTypes ?? []
        );
    const emissionsSourceTypeOptions =
        convertEmissionsSourceTypesToMultiSelectDropdownOptions(
            emissionsSourceItems?.emissionsSourceItems ?? [],
            emissionsSourceItems?.emissionsSourceTypes ?? [],
            emissionsSourceItems?.emissionsSourceCategories ?? []
        );
    const emissionsSourceCategoryOptions =
        convertEmissionsSourceCategoriesToMultiSelectDropdownOptions(
            emissionsSourceItems?.emissionsSourceCategories ?? []
        );

    const handleLocationSelect = (value: number) =>
        setLocationIds(toggleItemInArray(locationIds, value));

    const handleScopeSelect = (value: number) =>
        setScopes(toggleItemInArray(scopes, value));

    const handleItemSelect = (value: number) =>
        setItem(toggleItemInArray(item, value));

    const handleTypeSelect = (value: number) =>
        setType(toggleItemInArray(type, value));

    const handleCategorySelect = (value: number) =>
        setCategory(toggleItemInArray(category, value));

    const handleDataStatusSelect = (value: number) =>
        setDataStatus(toggleItemInArray(dataStatus, value));

    const clearAllFilters = () => {
        setSearch("");
        setLocationIds([]);
        setScopes([]);
        setItem([]);
        setType([]);
        setCategory([]);
        setDataStatus([]);
    };

    const showClearAllButton =
        search !== "" ||
        locationIds.length > 0 ||
        scopes.length > 0 ||
        item.length > 0 ||
        type.length > 0 ||
        category.length > 0 ||
        dataStatus.length > 0;

    const filterDropdowns = [
        <MultiSelectDropdown
            label="Location"
            selected={locationIds}
            options={locationOptions}
            onSelect={handleLocationSelect}
            onClear={() => setLocationIds([])}
            key="filter-dropdown-location"
        />,
        <MultiSelectDropdown
            label="Category"
            selected={category}
            options={emissionsSourceCategoryOptions}
            onSelect={handleCategorySelect}
            onClear={() => setCategory([])}
            key="filter-dropdown-category"
        />,
        <MultiSelectDropdown
            label="Type"
            selected={type}
            options={emissionsSourceTypeOptions}
            onSelect={handleTypeSelect}
            onClear={() => setType([])}
            key="filter-dropdown-type"
        />,
        <MultiSelectDropdown
            label="Item"
            selected={item}
            options={emissionsSourceItemOptions}
            onSelect={handleItemSelect}
            onClear={() => setItem([])}
            key="filter-dropdown-item"
        />,
        <MultiSelectDropdown
            label="Scope"
            selected={scopes}
            options={scopeOptions}
            onSelect={handleScopeSelect}
            onClear={() => setScopes([])}
            key="filter-dropdown-scope"
        />,
    ];

    if (!hideDataStatusFilter) {
        filterDropdowns.push(
            <MultiSelectDropdown
                label="Data Status"
                selected={dataStatus}
                options={dataStatusOptions}
                onSelect={handleDataStatusSelect}
                onClear={() => setDataStatus([])}
                key="filter-dropdown-data-status"
            />
        );
    }

    return (
        <>
            <div className="flex items-center p-5">
                <Button
                    variant="outline"
                    onClick={toggle}
                    {...triggerProps}
                    className="flex-none"
                >
                    <Funnel width={20} height={20} className="mr-2" />
                    {expanded ? "Hide" : "Show"} filters & sorting
                </Button>
                <div className="flex gap-2 flex-wrap pl-2">
                    {search !== "" && (
                        <FilterChip onRemove={() => setSearch("")}>
                            '{search}'
                        </FilterChip>
                    )}
                    <FilterChipGroup
                        selectedItemIds={locationIds}
                        allItems={locations}
                        labelKey="name"
                        onRemove={setLocationIds}
                    />
                    <FilterChipGroup
                        selectedItemIds={item}
                        allItems={
                            emissionsSourceItems?.emissionsSourceItems ?? []
                        }
                        labelKey="name"
                        onRemove={setItem}
                    />
                    <FilterChipGroup
                        selectedItemIds={type}
                        allItems={
                            emissionsSourceItems?.emissionsSourceTypes ?? []
                        }
                        labelKey="name"
                        onRemove={setType}
                    />
                    <FilterChipGroup
                        selectedItemIds={category}
                        allItems={
                            emissionsSourceItems?.emissionsSourceCategories ??
                            []
                        }
                        labelKey="name"
                        onRemove={setCategory}
                    />
                    <FilterChipGroup
                        allItems={scopeOptions.map((scope) => ({
                            ...scope,
                            id: scope.value,
                        }))}
                        selectedItemIds={scopes}
                        labelKey="label"
                        onRemove={setScopes}
                    />
                    <FilterChipGroup
                        selectedItemIds={dataStatus}
                        allItems={dataStatusOptions.map((_) => ({
                            ..._,
                            id: _.value,
                        }))}
                        labelKey="label"
                        onRemove={setDataStatus}
                    />
                    {showClearAllButton && (
                        <Button variant="outline" onClick={clearAllFilters}>
                            Clear all
                        </Button>
                    )}
                </div>
                <div className="ml-auto flex-none">{additionalActions}</div>
            </div>
            <Separator />
            <div
                className={clsx("pt-5 px-6", !expanded && "hidden")}
                {...expandableContentProps}
            >
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search..."
                    additionalSearchElements={
                        <ResponsiveGroupedItemsCarousel
                            items={filterDropdowns}
                            prevTooltipLabel="See previous group of filters"
                            nextTooltipLabel="See next group of filters"
                        />
                    }
                />
            </div>
        </>
    );
};

export default EmissionsSourcesFilter;
